import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import tableService, { RestaurantTable } from '../services/tableService'
import { useTranslation } from '../hooks/use-i18n'

interface Props {
  restaurantId: string
}

const RestaurantTablesSection: React.FC<Props> = ({ restaurantId }) => {
  const { t } = useTranslation()
  const [tables, setTables] = useState<RestaurantTable[]>([])
  const [label, setLabel] = useState('')
  const [loading, setLoading] = useState(true)

  const load = async () => {
    try {
      setTables(await tableService.listTables(restaurantId))
    } catch {
      toast.error(t('toast.tablesLoadFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [restaurantId])

  const handleCreate = async () => {
    if (!label.trim()) return
    try {
      await tableService.createTable(restaurantId, { label: label.trim() })
      setLabel('')
      toast.success(t('toast.tableCreated'))
      load()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.tableCreateFailed'))
    }
  }

  const handleDelete = async (tableId: string) => {
    if (!window.confirm(t('tables.deleteConfirm'))) return
    try {
      await tableService.deleteTable(restaurantId, tableId)
      toast.success(t('toast.tableDeleted'))
      load()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.tableDeleteFailed'))
    }
  }

  if (loading) return null

  return (
    <section className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 mt-8">
      <h2 className="text-xl font-bold text-white mb-4">{t('tables.title')}</h2>
      <p className="text-white/60 text-sm mb-4">{t('tables.subtitle')}</p>
      <div className="flex gap-2 mb-4">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('tables.labelPlaceholder')}
          className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
        />
        <button type="button" onClick={handleCreate} className="px-4 py-2 bg-sky-500 rounded-lg font-semibold">
          {t('tables.add')}
        </button>
      </div>
      <div className="space-y-3">
        {tables.map((table) => (
          <div key={table.id} className="flex flex-wrap items-center justify-between gap-3 bg-white/5 rounded-xl p-4">
            <div>
              <p className="font-semibold text-white">{table.label}</p>
              <p className="text-white/50 text-xs mt-1">{t('tables.seats', { count: String(table.seatsCount) })}</p>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={tableService.getTableQrUrl(table.qrToken)}
                target="_blank"
                rel="noreferrer"
                className="text-sky-400 text-sm"
              >
                {t('tables.qrLink')}
              </a>
              <button type="button" onClick={() => handleDelete(table.id)} className="text-red-400 text-sm">
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
        {tables.length === 0 && <p className="text-white/50 text-sm">{t('tables.empty')}</p>}
      </div>
    </section>
  )
}

export default RestaurantTablesSection
